import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardActions,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";
import { ReceiptLongRounded } from "@mui/icons-material";
import { ErrorContext } from "../../../components/GympinPagesProvider";
import { useSelector } from "react-redux";
import {
    transactionCorporate_query,
    transactionCorporate_queryExport,
} from "../../../network/api/TransactionsCorporate";
import { getRppChargeTransaction, SetRppChargeTransaction } from "../../../helper/pocket";
import _ChargeTransactionFilters from "./_ChargeTransactionFilters";
import _ChargeTransactionTableRow from "./_ChargeTransactionTableRow";

const _CorporateChargeTransactions = () => {
    const error = useContext(ErrorContext);
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(() => getRppChargeTransaction() || 10);
    const [sortBy, setSortBy] = useState({ Name: "Id", Desc: false });
    const [transactions, setTransactions] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [ticketStatus, setTicketStatus] = useState("");
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        getTransactions();
    }, [corporate, page, rowsPerPage, sortBy]);

    function getTransactions() {
        if (!corporate?.Id) return;
        setTransactions(null);
        transactionCorporate_query({
            queryType: "FILTER",
            Type: "DEPOSIT",
            MaxPrice: 0,
            FromDate: fromDate || undefined,
            ToDate: toDate || undefined,
            InvoiceStatus: ticketStatus || undefined,
            FinanceCorporateId: corporate.Id,
            paging: { Page: page, Size: rowsPerPage, orderBy: sortBy.Name, Desc: !sortBy.Desc }
        }).then((data) => {
            setTransactions(data.data.Data || {});
        }).catch(e => {
            setTransactions({ content: [] });
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دریافت لیست تراکنش‌ها" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function getExcel(e) {
        if (e) e.preventDefault();
        if (!corporate?.Id) return;
        setExporting(true);
        transactionCorporate_queryExport({
            queryType: "FILTER",
            Type: "DEPOSIT",
            MaxPrice: 0,
            FromDate: fromDate || undefined,
            ToDate: toDate || undefined,
            InvoiceStatus: ticketStatus || undefined,
            FinanceCorporateId: corporate.Id,
            paging: { Page: page, Size: rowsPerPage, orderBy: sortBy.Name, Desc: !sortBy.Desc }
        }).then((result) => {
            setExporting(false);
            const blob = new Blob([result.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${corporate.Name}-transactions-${new Date().toLocaleDateString('fa-IR')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }).catch(e => {
            setExporting(false);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دانلود خروجی اکسل" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    const isLoading = transactions === null;
    const transactionList = transactions?.content || [];
    const totalCount = transactions?.totalElements || 0;

    return (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 14px rgba(0, 0, 0, 0.03)',
                overflow: 'hidden'
            }}
        >
            {/* Filter & Action Toolbar */}
            <_ChargeTransactionFilters
                ticketStatus={ticketStatus}
                setTicketStatus={setTicketStatus}
                setFromDate={setFromDate}
                setToDate={setToDate}
                onApplyFilter={() => {
                    setPage(0);
                    getTransactions();
                }}
                onExportExcel={getExcel}
                exporting={exporting}
            />

            {/* Table */}
            <TableContainer>
                <Table aria-label="لیست تراکنش‌ها" sx={{ minWidth: 750 }}>
                    <TableHead>
                        <TableRow
                            sx={{
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                            }}
                        >
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem', width: 60 }}>
                                کاربر
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                نام کارمند
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                <TableSortLabel
                                    active={sortBy.Name === "Amount"}
                                    direction={sortBy.Desc ? "desc" : "asc"}
                                    onClick={() => setSortBy({ Name: "Amount", Desc: !sortBy.Desc })}
                                >
                                    مبلغ
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                <TableSortLabel
                                    active={sortBy.Name === "CreatedDate"}
                                    direction={sortBy.Desc ? "desc" : "asc"}
                                    onClick={() => setSortBy({ Name: "CreatedDate", Desc: !sortBy.Desc })}
                                >
                                    تاریخ و ساعت
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                وضعیت
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                خدمت / بلیط
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                مرکز ورزشی
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                    <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main', mb: 1.5 }} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        در حال دریافت لیست تراکنش‌ها...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : transactionList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(28, 139, 156, 0.08)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2,
                                            color: 'primary.main'
                                        }}
                                    >
                                        <ReceiptLongRounded sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                                        تراکنشی یافت نشد
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360, mx: 'auto' }}>
                                        در بازه زمانی و فیلترهای انتخابی هیچ تراکنشی ثبت نشده است.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactionList.map((row) => (
                                <_ChargeTransactionTableRow key={"depo-" + row.Id} row={row} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Footer */}
            {totalCount > 0 && (
                <CardActions
                    sx={{
                        p: 1.5,
                        px: 2.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        justifyContent: 'flex-end',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)'
                    }}
                >
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 20, 30, 50]}
                        count={totalCount}
                        labelRowsPerPage="تعداد در هر صفحه:"
                        labelDisplayedRows={({ from, to, count }) => `نمایش ${from} تا ${to} از ${count} تراکنش`}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            const newRpp = parseInt(event.target.value, 10);
                            setRowsPerPage(newRpp);
                            SetRppChargeTransaction(newRpp);
                            setPage(0);
                        }}
                    />
                </CardActions>
            )}
        </Card>
    );
};

export default _CorporateChargeTransactions;
